import { useState } from 'react'
import { DECK_FAILED, andList, bankPicture, cardItem, deckProblems, editDeck, handCard, knownCount, pictureSrc, standing, type Card, type Deck, type DeckEdit } from '@aurora/core'
import { auroraStore } from '../../store/auroraStore'
import { pushToast } from '../../lib/toast'
import { Sheet } from '../ui/Sheet'
import { CardFaceMini, DeckCover, deckHue } from './DeckCover'
import { StandingBar } from './Standing'
import { PlusIcon } from './TodayCards'
import type { DeckRow, PathHeld, useCards } from './useCards'

type Cards = ReturnType<typeof useCards>

/**
 * Her decks: what each is, how far along she is, and whether it is playing.
 * A deck opens into its ladder, where a parent can rest it or add a card of
 * their own (her friend's name, the word on the cereal box).
 */
export function DeckList({ cards, onNewDeck, onItem }: { cards: Cards; onNewDeck: () => void; onItem: (items: string[], title?: string) => void }) {
  const [open, setOpen] = useState<string | null>(null)
  const row = cards.decks.find((d) => d.artifact._id === open)

  return (
    <>
      <ul className="deck-grid">
        {cards.decks.map((d, i) => (
          <li key={d.artifact._id} className="reveal" style={{ '--i': i } as React.CSSProperties}>
            <DeckTile row={d} held={d.deck ? cards.held[d.deck.id] : undefined} onOpen={() => setOpen(d.artifact._id)} />
          </li>
        ))}
        <li className="reveal" style={{ '--i': cards.decks.length } as React.CSSProperties}>
          <button className="deck-tile deck-tile-new" onClick={onNewDeck}>
            <span className="deck-tile-new-mark" aria-hidden="true"><PlusIcon /></span>
            <span className="deck-tile-title">Start a new deck</span>
            <span className="deck-tile-direction">Anything: Hebrew letters, dinosaurs, times tables</span>
          </button>
        </li>
      </ul>

      {row && (
        <DeckSheet row={row} cards={cards} onClose={() => setOpen(null)} onItem={onItem} />
      )}
    </>
  )
}

function DeckTile({ row, held, onOpen }: { row: DeckRow; held?: PathHeld; onOpen: () => void }) {
  const { artifact, deck, status, day } = row
  const title = deck?.title ?? artifact.title
  // The line under the name is the deck's own sentence for a parent, when
  // it says more than the name. A list of card fronts is not a sentence, so
  // a deck without one shows just its name and counts; only a deck in
  // another script (Hebrew letters) shows a few of its faces, as glyphs,
  // since those letters are the most telling thing about it. The tile
  // clamps the line in CSS, so the ellipsis lands where the text ends in
  // either direction.
  const line = blurb(title, deck?.direction ?? artifact.generation?.prompt)
  const faces = line ? [] : firstCards(deck)
  const glyphs = faces.length > 0 && faces.some((t) => OTHER_SCRIPT.test(t))
  if (status === 'failed') return <FailedTile artifact={artifact} />
  return (
    <button
      className={`deck-tile is-${status}${deck?.paused ? ' is-paused' : ''}`}
      style={{ '--deck-hue': deckHue(deck?.id ?? title) } as React.CSSProperties}
      onClick={onOpen}
      disabled={status === 'generating'}
    >
      <span className="deck-tile-top">
        <DeckCover deck={deck} title={title} size={52} />
        {deck?.paused && <span className="deck-tile-flag">Resting</span>}
      </span>
      <span className="deck-tile-title" dir="auto">{title}</span>
      {/* dir="auto" lets a Hebrew or Arabic line lay out right to left, commas included. */}
      {line && <span className="deck-tile-direction" dir="auto">{line}</span>}
      {glyphs && (
        <span className="deck-tile-glyphs" aria-label={faces.join(', ')}>
          {faces.slice(0, 6).map((t, i) => <span key={i} className="deck-tile-glyph" dir="auto">{t}</span>)}
        </span>
      )}
      {status === 'generating' ? (
        <span className="deck-tile-making">
          <span className="deck-tile-making-bar" /> Writing the cards
        </span>
      ) : day ? (
        <>
          <StandingBar standings={day.standings} total={day.total} />
          <span className="deck-tile-meta tnum">
            {knownCount(day)} of {day.total} known
          </span>
          {/* Cards that wait for her reading path: all of them is why the deck is quiet; some is what comes later. */}
          {held && (
            <span className="deck-tile-wait">
              {held.cards >= day.total
                ? `Starts once her reading path reaches ${few(held.teaches)}`
                : `Joining as her reading path reaches ${held.teaches.length === 1 ? 'it' : 'them'}: ${few(held.teaches)}`}
            </span>
          )}
        </>
      ) : null}
    </button>
  )
}

/**
 * The line under a deck's name, when it says something the name does not.
 * A deck asked for in a word or two ("dinosaurs") keeps that as its
 * direction, and printing it under "Aurora's Dinosaurs" is an echo.
 */
function blurb(title: string | undefined, direction: string | undefined): string | undefined {
  const text = direction?.trim()
  if (!text) return undefined
  const stem = (w: string) => w.replace(/(es|s)$/, '')
  const named = new Set((title ?? '').toLowerCase().match(/\p{L}+/gu)?.map(stem) ?? [])
  const words = text.toLowerCase().match(/\p{L}+/gu) ?? []
  return words.length && words.every((w) => named.has(stem(w))) ? undefined : text
}

/** What waits, by name: a few, then how many more. */
const few = (items: string[]) => andList(items.length > 4 ? [...items.slice(0, 3), `${items.length - 3} more`] : items)

/** A deck by its first cards ("aleph, bet, gimel"), for one whose direction only repeats its name. */
function firstCards(deck: Deck | null | undefined): string[] {
  return (deck?.cards ?? []).map((c) => c.front.text?.trim()).filter((t): t is string => !!t).slice(0, 12)
}

/** Letters outside the Latin script: Hebrew, Arabic, Cyrillic, Greek, CJK and the rest. */
const OTHER_SCRIPT = /[^\p{Script=Latin}\p{Script=Common}\p{Script=Inherited}]/u

/** A deck that did not come back: say so, and offer the sentence again or letting it go. */
function FailedTile({ artifact }: { artifact: any }) {
  const direction: string = artifact.generation?.prompt ?? artifact.title
  const act = () => auroraStore.getState() as any
  return (
    <div className="deck-tile is-failed">
      <span className="deck-tile-top">
        <DeckCover deck={null} title={artifact.title} size={52} />
      </span>
      <span className="deck-tile-title">{artifact.title}</span>
      <span className="deck-tile-note">{DECK_FAILED}</span>
      <span className="deck-tile-actions">
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => {
            act().createDeck({ childId: artifact.childId, direction })
            act().deleteArtifact(artifact._id)
          }}
        >
          Try again
        </button>
        <button className="btn btn-quiet btn-sm" onClick={() => act().deleteArtifact(artifact._id)}>
          Remove
        </button>
      </span>
    </div>
  )
}

function DeckSheet({ row, cards, onClose, onItem }: { row: DeckRow; cards: Cards; onClose: () => void; onItem: (items: string[], title?: string) => void }) {
  const { artifact, deck, day } = row
  const [adding, setAdding] = useState(false)
  if (!deck || !day) return null

  const save = (edit: DeckEdit, done: string) => {
    ;(auroraStore.getState() as any).updateDeck({ artifactId: artifact._id, ...edit })
    pushToast(done)
  }

  const togglePause = () =>
    save({ paused: !deck.paused }, deck.paused ? `${deck.title} is back in today's cards` : `${deck.title} is resting`)

  return (
    <Sheet
      open
      onClose={onClose}
      eyebrow="Deck"
      title={deck.title}
      wide
      footer={
        <div className="deck-sheet-foot">
          <button className="btn btn-ghost" onClick={togglePause}>
            {deck.paused ? 'Play it again' : 'Rest this deck'}
          </button>
          <button className="btn btn-primary" onClick={() => setAdding(true)} disabled={adding}>
            <PlusIcon /> Add a card
          </button>
        </div>
      }
    >
      {blurb(deck.title, deck.direction) && <p className="deck-sheet-direction" dir="auto">{deck.direction}</p>}
      <div className="deck-sheet-stats tnum">
        <Stat n={knownCount(day)} label="she knows" />
        <Stat n={day.standings.learning + day.standings.shaky} label="on the way" />
        <Stat n={day.standings.new} label="still to meet" />
        <Stat n={deck.daily ?? 10} label="a day" />
      </div>

      {adding && (
        <AddCard
          deck={deck}
          onCancel={() => setAdding(false)}
          onAdd={(card) => {
            save({ card }, `"${card.front.text}" is the next new card in ${deck.title}`)
            setAdding(false)
          }}
        />
      )}

      <h3 className="deck-sheet-h">The ladder, in the order she meets it</h3>
      <ol className="deck-ladder">
        {deck.cards.map((c) => {
          const key = cardItem(deck.id, c.id)
          const st = standing(cards.memory[key], cards.now)
          return (
            <li key={c.id}>
              <button className={`deck-ladder-card is-${st}`} onClick={() => onItem([key])} title={c.front.text ?? c.id}>
                <CardFaceMini face={c.kind === 'generator' ? { text: genGlyph(c) } : c.front} assets={artifact.assets} />
              </button>
            </li>
          )
        })}
      </ol>
    </Sheet>
  )
}

function genGlyph(c: Card): string {
  switch (c.gen?.type) {
    case 'add': return '+'
    case 'subtract': return '−'
    case 'count': return '123'
    case 'after': return '→'
    case 'before': return '←'
    case 'rhyme': return '~'
    default: return '◆◇'
  }
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="deck-stat">
      <span className="deck-stat-n">{n}</span>
      <span className="deck-stat-label">{label}</span>
    </div>
  )
}

/**
 * One card, by hand: what she reads, and, when it is not a word the clay
 * bank already draws, a few words describing the picture for the back.
 * It goes to the top of the ladder, so she meets it next.
 */
function AddCard({ deck, onAdd, onCancel }: { deck: Deck; onAdd: (card: Card) => void; onCancel: () => void }) {
  const [text, setText] = useState('')
  const [draw, setDraw] = useState('')
  const clean = text.trim()
  const card = handCard(deck, { text: clean, draw })
  const bank = card.back?.picture && bankPicture(card.back.picture) ? pictureSrc(card.back.picture) : null
  const problems = clean ? deckProblems(editDeck(deck, { card })) : []

  return (
    <form
      className="add-card"
      onSubmit={(e) => {
        e.preventDefault()
        if (clean && !problems.length) onAdd(card)
      }}
    >
      <div className="add-card-preview" aria-hidden="true">
        <span className="add-card-front" style={{ fontSize: clean.length > 8 ? '1rem' : clean.length > 4 ? '1.25rem' : undefined }}>{clean || 'Aa'}</span>
        {bank ? <img src={bank} alt="" /> : draw.trim() ? <span className="add-card-draw">{draw}</span> : null}
      </div>
      <div className="add-card-fields">
        <label className="cards-field">
          <span className="cards-field-label">What she reads</span>
          <input className="field-input" autoFocus value={text} maxLength={80} onChange={(e) => setText(e.target.value)} placeholder="Grandma, Mila, tractor, 7" />
        </label>
        {!bank && (
          <label className="cards-field">
            <span className="cards-field-label">The picture on the back <span className="subtle">(optional)</span></span>
            <input className="field-input" value={draw} maxLength={240} onChange={(e) => setDraw(e.target.value)} placeholder="Grandma in her red garden hat" />
            <span className="field-hint">Sapling draws it in clay, like the rest of her cards.</span>
          </label>
        )}
        {problems.length > 0 && <p className="field-error">{problems[0]}</p>}
        <div className="add-card-actions">
          <button type="button" className="btn btn-quiet" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={!clean || problems.length > 0}>Add, she meets it next</button>
        </div>
      </div>
    </form>
  )
}
